import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  TextField,
  Button,
  Typography,
  FormControlLabel,
  Switch,
  IconButton,
  MenuItem,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  Alert,
} from "@mui/material";
import { ModalBox } from "../../../styles/ModalStyles";
import CloseIcon from "@mui/icons-material/Close";

const EditarUsuarioModal = ({ open, onClose, onSubmit, usuario }) => {
  const [nombre, setNombre] = useState("");
  const [apellido, setApellido] = useState("");
  const [correo, setCorreo] = useState("");
  const [rol, setRol] = useState("");
  const [habilitado, setHabilitado] = useState(false);
  const [cambiarContraseña, setCambiarContraseña] = useState(false);
  const [contraseña, setContraseña] = useState("");
  const [confirmarContraseña, setConfirmarContraseña] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [esProveedorExterno, setEsProveedorExterno] = useState(false);
  const [proveedorExterno, setProveedorExterno] = useState("");
  const [proveedorOriginal, setProveedorOriginal] = useState("");
  const [cambioTipoAutenticacion, setCambioTipoAutenticacion] = useState(false);

  useEffect(() => {
    if (usuario) {
      setNombre(usuario.nombre);
      setApellido(usuario.apellido);
      setCorreo(usuario.correo);
      setRol(usuario.rol);
      setHabilitado(usuario.habilitado);
      
      // Verificar si el usuario tiene un proveedor externo
      const tieneProveedorExterno = usuario.proveedorExterno && usuario.proveedorExterno !== "";
      setEsProveedorExterno(tieneProveedorExterno);
      setProveedorExterno(tieneProveedorExterno ? usuario.proveedorExterno : "");
      setProveedorOriginal(tieneProveedorExterno ? usuario.proveedorExterno : "");
      
      // Reiniciamos los campos de contraseña cuando se abre el modal
      setCambiarContraseña(false);
      setContraseña("");
      setConfirmarContraseña("");
      setPasswordError("");
      setCambioTipoAutenticacion(false);
    }
  }, [usuario]);

  // Detectar cambios en el tipo de autenticación
  useEffect(() => {
    if (usuario) {
      const usuarioEraExterno = usuario.proveedorExterno && usuario.proveedorExterno !== "";
      setCambioTipoAutenticacion(usuarioEraExterno !== esProveedorExterno);
      
      // Si cambia de externo a local, asegurarse de que se solicite una contraseña
      if (usuarioEraExterno && !esProveedorExterno) {
        setCambiarContraseña(true);
      }
    }
  }, [esProveedorExterno, usuario]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validaciones específicas según el tipo de usuario
    if (!esProveedorExterno && cambiarContraseña) {
      if (contraseña !== confirmarContraseña) {
        setPasswordError("Las contraseñas no coinciden");
        return;
      }
      if (contraseña.length < 6) {
        setPasswordError("La contraseña debe tener al menos 6 caracteres");
        return;
      }
    }
    
    // Si cambia de externo a local, debe proporcionar una contraseña
    if (cambioTipoAutenticacion && !esProveedorExterno && !cambiarContraseña) {
      setPasswordError("Debe establecer una contraseña al cambiar a usuario local");
      setCambiarContraseña(true);
      return;
    }
    
    // Si es externo, asegurarse de que se seleccionó un proveedor
    if (esProveedorExterno && !proveedorExterno) {
      return; // No proceder si no se seleccionó un proveedor
    }
    
    // Enviar datos con o sin contraseña según corresponda
    const userData = {
      ...usuario,
      nombre,
      apellido,
      correo,
      rol,
      habilitado,
      esProveedorExterno,
      proveedorExterno: esProveedorExterno ? proveedorExterno : null,
      contraseña: cambiarContraseña ? contraseña : null,
    };
    
    onSubmit(userData);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalBox>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        <Typography variant="h6">Editar usuario</Typography>
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Nombre"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Apellido"
            value={apellido}
            onChange={(e) => setApellido(e.target.value)}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Correo"
            value={correo}
            onChange={(e) => setCorreo(e.target.value)}
            margin="normal"
            required
            type="email"
          />
          <TextField
            fullWidth
            select
            label="Rol"
            value={rol}
            onChange={(e) => setRol(e.target.value)}
            margin="normal"
            required
          >
            <MenuItem value="Usuario">Usuario</MenuItem>
            <MenuItem value="Administrador">Administrador</MenuItem>
          </TextField>
          
          <FormControlLabel
            control={
              <Checkbox
                checked={esProveedorExterno}
                onChange={(e) => setEsProveedorExterno(e.target.checked)}
              />
            }
            label="Usuario con proveedor externo"
          />
          
          {esProveedorExterno ? (
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Proveedor</InputLabel>
              <Select
                value={proveedorExterno}
                label="Proveedor"
                onChange={(e) => setProveedorExterno(e.target.value)}
              >
                <MenuItem value="Google">Google</MenuItem>
                <MenuItem value="Microsoft">Microsoft</MenuItem>
              </Select>
            </FormControl>
          ) : (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={cambiarContraseña}
                    onChange={(e) => {
                      setCambiarContraseña(e.target.checked);
                      if (!e.target.checked) {
                        setPasswordError("");
                      }
                    }}
                    disabled={cambioTipoAutenticacion && !esProveedorExterno}
                  />
                }
                label="Cambiar contraseña"
              />
              
              {(cambiarContraseña || (cambioTipoAutenticacion && !esProveedorExterno)) && (
                <>
                  <TextField
                    fullWidth
                    label="Nueva contraseña"
                    type="password"
                    value={contraseña}
                    onChange={(e) => {
                      setContraseña(e.target.value);
                      setPasswordError("");
                    }}
                    margin="normal"
                    required
                  />
                  <TextField
                    fullWidth
                    label="Confirmar contraseña"
                    type="password"
                    value={confirmarContraseña}
                    onChange={(e) => {
                      setConfirmarContraseña(e.target.value);
                      setPasswordError("");
                    }}
                    margin="normal"
                    required
                    error={!!passwordError}
                    helperText={passwordError}
                  />
                </>
              )}
            </>
          )}
          
          <FormControlLabel
            control={
              <Switch
                checked={habilitado}
                onChange={(e) => setHabilitado(e.target.checked)}
                color="primary"
              />
            }
            label={habilitado ? "Habilitado" : "Deshabilitado"}
          />
          
          {cambioTipoAutenticacion && (
            <Alert severity="warning">
              ¡Atención! Está cambiando el tipo de autenticación del usuario.
              {proveedorOriginal && !esProveedorExterno 
                ? " Este usuario ya no podrá iniciar sesión con " + proveedorOriginal + " y deberá usar la contraseña establecida."
                : " Este usuario ya no usará contraseña local y deberá iniciar sesión con " + proveedorExterno + "."}
            </Alert>
          )}
          
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ mr: 1 }}
              disabled={
                (cambiarContraseña && contraseña !== confirmarContraseña) ||
                (esProveedorExterno && !proveedorExterno) ||
                (cambioTipoAutenticacion && !esProveedorExterno && (!cambiarContraseña || !contraseña))
              }
            >
              Guardar
            </Button>
            <Button onClick={onClose} variant="contained" color="error">
              Cancelar
            </Button>
          </Box>
        </form>
      </ModalBox>
    </Modal>
  );
};

export default EditarUsuarioModal;